import {
  Box,
  Button,
  Card,
  CircularProgress,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { auth } from './firebase';
import { useGetUserRoles, useSignOut } from './api/hooks/auth';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import ThriveLogo from '../assets/thrive-logo.svg';
import {
  Bookmark,
  Group,
  HelpOutline,
  Inventory,
  LogoutOutlined,
  Sell,
} from '@mui/icons-material';
import SupportForm from './components/SupportForm';

const Container = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: userRoles, isLoading: isLoadingUserRoles } =
    useGetUserRoles(auth);

  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [showSupportForm, setShowSupportForm] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      setUser(authUser);
    });

    return () => {
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (!userRoles) return;
    if (userRoles['internal_user']) return;
    if (
      !userRoles['clover_bc'] &&
      ['products', 'organizations', 'brands'].some((path) =>
        pathname.includes(path)
      )
    )
      navigate('/merchants');
  }, [pathname, userRoles, navigate]);

  const { mutate: logout } = useSignOut(auth, {
    onSuccess() {
      navigate('/login');
    },
    onError() {
      console.error('Could not log out');
    },
  });

  const navLinks: { to: string; icon: ReactNode; label: string }[] =
    useMemo(() => {
      if (!userRoles) return [];
      return [
        ...(userRoles['internal_user']
          ? [
              { to: '/products', icon: <Inventory />, label: 'Product' },
              {
                to: '/organizations',
                icon: <Group />,
                label: 'Organizations',
              },
              { to: '/brands', icon: <Bookmark />, label: 'Brands' },
            ]
          : []),
        ...(userRoles['clover_bc']
          ? [
              {
                to: '/merchants',
                icon: <Sell />,
                label: 'Merchants',
              },
            ]
          : []),
      ];
    }, [userRoles]);

  // if undefined then auth state is loading, if null then user is logged out
  if (user === undefined || isLoadingUserRoles)
    return (
      <Box width="100%" height="100vh" textAlign="center">
        <CircularProgress />
      </Box>
    );
  if (user)
    return (
      <Box display="flex">
        <Drawer
          sx={{
            width: 150,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 150,
              boxSizing: 'border-box',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Box mx={2} mt={5} mb={3}>
            <ThriveLogo />
          </Box>
          {navLinks.map(({ to, icon, label }) => (
            <Button
              key={label}
              component={NavLink}
              to={to}
              variant="text"
              sx={{
                borderRadius: 0,
                justifyContent: 'start',
                m: 0,
                borderLeft: '5px solid transparent',
                '&.active': {
                  background: '#F3FAF3',
                  color: '#00B74F',
                  borderLeft: '5px solid #00B74F',
                },
              }}
              startIcon={icon}
            >
              {label}
            </Button>
          ))}
          <Box flexGrow={1} />
          <Typography m={2} fontWeight={700} textAlign="center">
            {auth.currentUser?.displayName}
          </Typography>
          <Button
            startIcon={<HelpOutline />}
            onClick={() => setShowSupportForm(true)}
            variant="text"
            sx={{ borderRadius: 0, justifyContent: 'start', m: 0 }}
          >
            Need Help?
          </Button>
          <Button
            startIcon={<LogoutOutlined />}
            onClick={() => logout()}
            variant="text"
            sx={{ borderRadius: 0, justifyContent: 'start', m: 0, mb: 3 }}
          >
            Sign out
          </Button>
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Outlet />
        </Box>
        <SupportForm
          open={showSupportForm}
          onClose={() => setShowSupportForm(false)}
        />
      </Box>
    );
  return (
    <Card elevation={7} sx={{ mx: 'auto', mt: 10, p: 5, width: 400 }}>
      <Stack>
        <Typography>Not signed in</Typography>
        <Button onClick={() => navigate('login')}>Sign in</Button>
      </Stack>
    </Card>
  );
};

export default Container;
